/**
 * CRM API client — wraps /api/crm/* endpoints.
 *
 * Uses authFetch() for authentication. All functions return typed data.
 */

import { qs, fetchJson } from './http';

// ─── Types ───────────────────────────────────────────────

export interface CrmCompany {
  id: number;
  customer_no: string | null;
  name: string;
  domain: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  industry: string | null;
  company_size: string | null;
  website: string | null;
  phone: string | null;
  address: string | null;
  status: string | null;
  agent_level: string | null;
  quote_level: string | null;
  registered_at: string | null;
  consignee_type: string | null;
  consignee_company: string | null;
  consignee_name: string | null;
  consignee_email: string | null;
  consignee_phone: string | null;
  consignee_address: string | null;
  last_followup_at: string | null;
  last_followup_purpose: string | null;
  last_followup_result: string | null;
  next_followup_at: string | null;
  sales_amount: number | null;
  tags: string;
  source: string | null;
  ai_summary: string | null;
  enrichment_data: string;
  enriched_at: string | null;
  notes: string | null;
  external_id: string | null;
  external_source: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  contact_count?: number;
}

export interface CrmContact {
  id: number;
  company_id: number | null;
  name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  department: string | null;
  address: string | null;
  is_primary: boolean;
  timezone: string | null;
  language: string | null;
  tags: string;
  source: string | null;
  ai_profile: string | null;
  last_contacted_at: string | null;
  notes: string | null;
  external_id: string | null;
  external_source: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  company_name?: string | null;
}

export interface CrmFollowup {
  id: number;
  company_id: number;
  contact_id: number | null;
  deal_id: number | null;
  purpose: string | null;
  result: string | null;
  outcome: string | null;
  next_followup_at: string | null;
  next_goal: string | null;
  followed_again: boolean;
  attachments: string;
  created_by: string;
  occurred_at: string;
  created_at: string;
  company_name?: string | null;
}

export interface CrmProduct {
  id: number;
  product_no: string | null;
  name: string;
  name_cn: string | null;
  name_en: string | null;
  brand: string | null;
  brand_type: string | null;
  category: string | null;
  default_unit_price: number | null;
  currency: string;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  net_weight_kg: number | null;
  volume_m3: number | null;
  carton_capacity: number | null;
  carton_weight_kg: number | null;
  carton_length_cm: number | null;
  carton_width_cm: number | null;
  carton_height_cm: number | null;
  carton_volume_m3: number | null;
  hs_code: string | null;
  surface_material: string | null;
  default_cargo_name: string | null;
  default_remark: string | null;
  notes: string | null;
  is_active: boolean;
  tags: string;
  external_id: string | null;
  external_source: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CrmInteraction {
  id: number;
  contact_id: number;
  company_id: number | null;
  type: string;
  subject: string | null;
  content: string | null;
  content_preview: string | null;
  sentiment: string | null;
  intent: string | null;
  email_message_id: string | null;
  email_account_id: number | null;
  metadata: string;
  created_by: string | null;
  occurred_at: string;
  created_at: string;
}

export interface CrmDeal {
  id: number;
  deal_number: string;
  contact_id: number | null;
  company_id: number | null;
  customer_no: string | null;
  title: string;
  type: string;
  stage: string;
  source_deal_id: number | null;
  amount: number | null;
  goods_subtotal: number | null;
  currency: string;
  probability: number | null;
  tax_rate: number | null;
  tax_amount: number | null;
  freight: number | null;
  bank_fee: number | null;
  discount_rate: number | null;
  discount_amount: number | null;
  deposit_ratio: number | null;
  balance_ratio: number | null;
  deposit_amount: number | null;
  balance_amount: number | null;
  received_amount: number | null;
  delivery_method: string | null;
  payment_method: string | null;
  payment_terms: string | null;
  shipping_terms: string | null;
  quote_valid_days: number | null;
  valid_until: string | null;
  expected_delivery_days: number | null;
  expected_delivery_date: string | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  confirm_status: string | null;
  our_company: string | null;
  sales_rep: string | null;
  owner_user: string | null;
  ship_to_option: string | null;
  consignee_company: string | null;
  consignee_name: string | null;
  consignee_phone: string | null;
  consignee_email: string | null;
  consignee_address: string | null;
  notes: string | null;
  metadata: string;
  external_id: string | null;
  external_source: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  company_name?: string | null;
  contact_name?: string | null;
}

export interface CrmDealItem {
  id: number;
  deal_id: number;
  product_id: number | null;
  product_name: string;
  sku: string | null;
  description: string | null;
  remark: string | null;
  adapter: string | null;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  line_total: number;
  volume_m3: number | null;
  weight_kg: number | null;
  sort_order: number;
}

export interface CrmActivity {
  id: number;
  entity_type: string;
  entity_id: number;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  user_id: string;
  created_at: string;
}

export interface CrmStats {
  total_companies: number;
  total_contacts: number;
  total_deals: number;
  total_products: number;
  active_deals_amount: number;
  won_deals_amount: number;
  new_contacts_this_month: number;
  followups_due: number;
}

export interface CrmPipelineItem {
  stage: string;
  count: number;
  total_amount: number;
}

// ─── Stats ───────────────────────────────────────────────

export function fetchCrmStats(): Promise<CrmStats> {
  return fetchJson('/api/crm/stats');
}

export function fetchCrmPipeline(): Promise<{ pipeline: CrmPipelineItem[] }> {
  return fetchJson('/api/crm/pipeline');
}

// ─── Companies ───────────────────────────────────────────

export function fetchCompanies(opts?: {
  search?: string;
  country?: string;
  industry?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmCompany[]; total: number }> {
  return fetchJson('/api/crm/companies' + qs(opts || {}));
}

export function fetchCompanyDetail(id: number): Promise<{
  company: CrmCompany;
  contacts: CrmContact[];
  deals: CrmDeal[];
  followups: CrmFollowup[];
  interactions: CrmInteraction[];
  activities: CrmActivity[];
}> {
  return fetchJson(`/api/crm/companies/${id}`);
}

export function createCompany(data: Partial<CrmCompany>): Promise<{ company: CrmCompany }> {
  return fetchJson('/api/crm/companies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateCompany(id: number, data: Partial<CrmCompany>): Promise<{ company: CrmCompany }> {
  return fetchJson(`/api/crm/companies/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function deleteCompany(id: number): Promise<{ success: boolean }> {
  return fetchJson(`/api/crm/companies/${id}`, { method: 'DELETE' });
}

// ─── Contacts ────────────────────────────────────────────

export function fetchContacts(opts?: {
  search?: string;
  company_id?: number;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmContact[]; total: number }> {
  return fetchJson('/api/crm/contacts' + qs(opts || {}));
}

export function fetchContactDetail(id: number): Promise<{
  contact: CrmContact;
  company: CrmCompany | null;
  interactions: CrmInteraction[];
  deals: CrmDeal[];
  followups: CrmFollowup[];
  activities: CrmActivity[];
}> {
  return fetchJson(`/api/crm/contacts/${id}`);
}

export function createContact(data: Partial<CrmContact>): Promise<{ contact: CrmContact }> {
  return fetchJson('/api/crm/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateContact(id: number, data: Partial<CrmContact>): Promise<{ contact: CrmContact }> {
  return fetchJson(`/api/crm/contacts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ─── Deals ───────────────────────────────────────────────

export function fetchDeals(opts?: {
  stage?: string;
  type?: string;
  contact_id?: number;
  company_id?: number;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmDeal[]; total: number }> {
  return fetchJson('/api/crm/deals' + qs(opts || {}));
}

export function fetchDealDetail(id: number): Promise<{
  deal: CrmDeal;
  items: CrmDealItem[];
  company: CrmCompany | null;
  contact: CrmContact | null;
  activities: CrmActivity[];
}> {
  return fetchJson(`/api/crm/deals/${id}`);
}

export function createDeal(data: Partial<CrmDeal> & { items?: Partial<CrmDealItem>[] }): Promise<{ deal: CrmDeal }> {
  return fetchJson('/api/crm/deals', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateDeal(
  id: number,
  data: Partial<CrmDeal> & { items?: Partial<CrmDealItem>[] },
): Promise<{ deal: CrmDeal }> {
  return fetchJson(`/api/crm/deals/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ─── Interactions ────────────────────────────────────────

export function fetchInteractions(opts?: {
  contact_id?: number;
  company_id?: number;
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmInteraction[]; total: number }> {
  return fetchJson('/api/crm/interactions' + qs(opts || {}));
}

export function createInteraction(data: Partial<CrmInteraction>): Promise<{ interaction: CrmInteraction }> {
  return fetchJson('/api/crm/interactions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ─── Products ────────────────────────────────────────────

export function fetchProducts(opts?: {
  search?: string;
  category?: string;
  brand?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmProduct[]; total: number }> {
  const { is_active, ...rest } = opts || {};
  return fetchJson(
    '/api/crm/products' + qs({ ...rest, is_active: is_active === undefined ? undefined : String(is_active) }),
  );
}

export function fetchProductDetail(id: number): Promise<{ product: CrmProduct }> {
  return fetchJson(`/api/crm/products/${id}`);
}

export function createProduct(data: Partial<CrmProduct>): Promise<{ product: CrmProduct }> {
  return fetchJson('/api/crm/products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateProduct(id: number, data: Partial<CrmProduct>): Promise<{ product: CrmProduct }> {
  return fetchJson(`/api/crm/products/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function deleteProduct(id: number): Promise<{ success: boolean }> {
  return fetchJson(`/api/crm/products/${id}`, { method: 'DELETE' });
}

// ─── Followups (跟进) ────────────────────────────────────

export function fetchFollowups(opts?: {
  company_id?: number;
  contact_id?: number;
  due_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ items: CrmFollowup[]; total: number }> {
  const { due_only, ...rest } = opts || {};
  return fetchJson('/api/crm/followups' + qs({ ...rest, due_only: due_only ? 'true' : undefined }));
}

export function createFollowup(data: Partial<CrmFollowup>): Promise<{ followup: CrmFollowup }> {
  return fetchJson('/api/crm/followups', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function updateFollowup(id: number, data: Partial<CrmFollowup>): Promise<{ followup: CrmFollowup }> {
  return fetchJson(`/api/crm/followups/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export function deleteFollowup(id: number): Promise<{ success: boolean }> {
  return fetchJson(`/api/crm/followups/${id}`, { method: 'DELETE' });
}
